import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getEmployeeGrainVarieties,
  getEmployeeGrainVariety,
  createGrainVariety,
  updateGrainVariety,
  deleteGrainVariety as deleteGrainVarietyService,
} from '../services/grainVarieties';
import { logError } from '../utils/errorHandler';

const useGrainVarieties = ({ autoFetch = true } = {}) => {
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, page: 1 });
  const hasLoadedRef = useRef(false);

  const fetchVarieties = useCallback(async (params = {}) => {
    try {
      setError(null);
      if (hasLoadedRef.current) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await getEmployeeGrainVarieties(params);
      setVarieties(data.results || []);
      setPagination({ count: data.count || 0, page: params.page || 1 });
    } catch (err) {
      logError('Fetch grain varieties', err);
      setError(err);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const fetchVariety = useCallback(async (id) => {
    try {
      setError(null);
      setLoading(true);
      return await getEmployeeGrainVariety(id);
    } catch (err) {
      logError('Fetch grain variety', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveVariety = useCallback(async (data, id = null) => {
    try {
      setError(null);
      setIsSaving(true);
      return id ? await updateGrainVariety(id, data) : await createGrainVariety(data);
    } catch (err) {
      logError('Save grain variety', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const removeVariety = useCallback(async (id) => {
    try {
      setError(null);
      setIsDeleting(true);
      await deleteGrainVarietyService(id);
    } catch (err) {
      logError('Delete grain variety', err);
      setError(err);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (autoFetch) fetchVarieties();
  }, [autoFetch, fetchVarieties]);

  return {
    varieties,
    loading,
    isRefreshing,
    isSaving,
    isDeleting,
    error,
    pagination,
    fetchVarieties,
    fetchVariety,
    saveVariety,
    removeVariety,
    clearError,
  };
};

export default useGrainVarieties;
